"use client";

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Brain } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ReasoningProps {
  children?: React.ReactNode;
  isStreaming?: boolean;
  defaultOpen?: boolean;
  className?: string;
  // New enhanced props
  thinkingTime?: number;
  reasoning?: string | string[];
  autoExpand?: boolean;
}

interface ReasoningTriggerProps {
  title?: string;
  duration?: number;
  className?: string;
}

interface ReasoningContentProps {
  children?: React.ReactNode;
  className?: string;
  reasoning?: string | string[];
}

const ReasoningContext = React.createContext<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isStreaming: boolean;
  duration?: number;
}>({ isOpen: false, setIsOpen: () => {}, isStreaming: false });

export function Reasoning({
  children,
  isStreaming = false,
  defaultOpen = false,
  className,
  thinkingTime,
  reasoning,
  autoExpand = false
}: ReasoningProps) {
  console.log('Reasoning: Component props', { isStreaming, thinkingTime, reasoning: reasoning?.toString().substring(0, 50) });
  // Auto-open when streaming starts
  const [isOpen, setIsOpen] = useState(defaultOpen || isStreaming);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [duration, setDuration] = useState<number>(thinkingTime || 0);
  const [liveTimer, setLiveTimer] = useState<number>(0);

  // Auto-expand effect when streaming begins
  useEffect(() => {
    if (isStreaming && !isOpen) {
      setIsOpen(true);
    }
  }, [isStreaming, isOpen]);

  // Auto-expand functionality
  useEffect(() => {
    if (autoExpand && !isOpen) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000); // 1 second delay for auto-expand
      return () => clearTimeout(timer);
    }
  }, [autoExpand, isOpen]);

  // Timer management effect
  useEffect(() => {
    console.log('Reasoning: Timer effect triggered', { isStreaming, duration, liveTimer, startTime });
    let interval: NodeJS.Timeout;

    if (isStreaming) {
      // Start timing when streaming begins
      if (!startTime) {
        console.log('Reasoning: Starting timer NOW');
        const now = Date.now();
        setStartTime(now);
        setLiveTimer(1); // Start at 1 second
      }

      // Update timer every second while streaming
      interval = setInterval(() => {
        setLiveTimer(prev => {
          const newValue = prev + 1;
          console.log('Reasoning: Timer tick', newValue);
          return newValue;
        });
      }, 1000);
    } else if (!isStreaming && startTime) {
      // Stop timing when streaming ends
      console.log('Reasoning: Stopping timer');
      const finalDuration = thinkingTime || liveTimer;
      setDuration(finalDuration); // Use seconds
      setStartTime(null);
      setLiveTimer(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, thinkingTime]);

  // Pass the live timer or final duration to context
  const contextValue = {
    isOpen,
    setIsOpen,
    isStreaming,
    duration: isStreaming ? liveTimer : duration // Show seconds for both live and final
  };

  return (
    <ReasoningContext.Provider value={contextValue}>
      <div className={cn(
        "transition-all duration-300 ease-in-out",
        className
      )}>
        {children || (
          <>
            <ReasoningTrigger title="Thought" />
            <ReasoningContent reasoning={reasoning} />
          </>
        )}
      </div>
    </ReasoningContext.Provider>
  );
}

export function ReasoningTrigger({
  title = "Thinking",
  duration: propDuration,
  className
}: ReasoningTriggerProps) {
  const { isOpen, setIsOpen, isStreaming, duration } = React.useContext(ReasoningContext);
  const displayDuration = propDuration || duration;

  // Show "Thought" when complete, "Thinking" while streaming
  const displayTitle = isStreaming ? title : "Thought";

  return (
    <button
      onClick={() => setIsOpen(!isOpen)}
      className={cn(
        "flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors",
        "border border-gray-200 rounded-lg hover:bg-gray-50",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        className
      )}
      aria-expanded={isOpen}
      aria-controls="reasoning-content"
      tabIndex={0}
      aria-label={`${displayTitle} ${displayDuration ? `(${displayDuration}s)` : ''} - ${isOpen ? 'collapse' : 'expand'} reasoning`}
    >
      <Brain
        className="w-4 h-4"
        aria-hidden="true"
      />
      <span>{displayTitle}</span>
      {displayDuration && (
        <span className="text-xs text-gray-500">({displayDuration}s)</span>
      )}
      {isStreaming && (
        <div 
          className="w-2 h-2 bg-blue-500 rounded-full animate-pulse ml-1" 
          aria-hidden="true"
          role="status"
        />
      )}
      {isOpen ? (
        <ChevronDown className="w-4 h-4 ml-auto" aria-hidden="true" />
      ) : (
        <ChevronRight className="w-4 h-4 ml-auto" aria-hidden="true" />
      )}
    </button>
  );
}

export function ReasoningContent({ children, className, reasoning }: ReasoningContentProps) {
  const { isOpen } = React.useContext(ReasoningContext);

  if (!isOpen) return null;

  const renderReasoning = () => {
    if (children) return children;
    if (!reasoning) return null;

    if (Array.isArray(reasoning)) {
      return reasoning.map((step, index) => (
        <p key={index} className="mb-3 last:mb-0 leading-relaxed">
          {step}
        </p>
      ));
    }

    // Handle string reasoning with paragraph breaks
    return reasoning.split('\n\n').map((paragraph, index) => (
      <p key={index} className="mb-3 last:mb-0 leading-relaxed">
        {paragraph.trim()}
      </p>
    ));
  };

  return (
    <div 
      id="reasoning-content"
      className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
      )}
      role="region"
      aria-label="AI reasoning content"
    >
      <div className={cn(
        "px-4 pb-4 text-sm text-gray-700 leading-relaxed",
        "prose prose-sm max-w-none prose-p:my-2 prose-p:leading-relaxed",
        className
      )}>
        <div className="pt-1">
          {renderReasoning()}
        </div>
      </div>
    </div>
  );
}